import React, { useState } from 'react';
import { Calendar, Users, Clock, MapPin, Star, Info, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../../components/ui/Button';


const BookingSummary = ({ bookingData, paymentData }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not selected';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateSubtotal = () => {
    const basePrice = (bookingData?.basePrice || 0) * (bookingData?.adults || 0);
    const childrenPrice = (bookingData?.basePrice || 0) * 0.7 * (bookingData?.children || 0);
    const addOnsPrice = bookingData?.addOns?.reduce((sum, addon) => sum + (addon?.price || 0), 0) || 0;
    return basePrice + childrenPrice + addOnsPrice;
  };

  const subtotal = calculateSubtotal();
  const taxRate = 0.08; // 8% tax
  const taxes = subtotal * taxRate;
  const serviceFee = 15;
  const total = subtotal + taxes + serviceFee;

  const refundPolicy = {
    fullRefund: '7+ days before: 100% refund',
    partialRefund: '3-7 days before: 50% refund',
    noRefund: 'Less than 3 days: Non-refundable'
  };

  return (
    <div className="space-y-6">
      {/* Tour Summary Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0">
              <img
                src="/api/placeholder/80/80"
                alt={bookingData?.tourName}
                className="w-full h-full object-cover rounded-lg"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {bookingData?.tourName || 'Selected Tour'}
              </h3>
              <div className="flex items-center space-x-1 mt-1">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="text-sm text-gray-600">4.8 (324 reviews)</span>
              </div>
              <div className="flex items-center space-x-1 mt-1 text-sm text-gray-500">
                <MapPin className="w-4 h-4" />
                <span>Downtown Area</span>
              </div>
            </div>
          </div>

          {/* Booking Details */}
          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>Date</span>
              </div>
              <span className="font-medium text-gray-900">
                {formatDate(bookingData?.selectedDate)}
              </span>
            </div>

            {bookingData?.selectedTime && (
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Time</span>
                </div>
                <span className="font-medium text-gray-900">
                  {bookingData?.selectedTime}
                </span>
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2 text-gray-600">
                <Users className="w-4 h-4" />
                <span>Travelers</span>
              </div>
              <span className="font-medium text-gray-900">
                {bookingData?.adults || 0} Adult{(bookingData?.adults || 0) !== 1 ? 's' : ''}
                {(bookingData?.children || 0) > 0 && `, ${bookingData?.children} Child${bookingData?.children !== 1 ? 'ren' : ''}`}
                {(bookingData?.infants || 0) > 0 && `, ${bookingData?.infants} Infant${bookingData?.infants !== 1 ? 's' : ''}`}
              </span>
            </div>
          </div>

          {/* Add-ons */}
          {bookingData?.addOns?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Selected Add-ons</h4>
              <div className="space-y-2">
                {bookingData?.addOns?.map((addon, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-gray-600">{addon?.name}</span>
                    <span className="font-medium text-gray-900">+${addon?.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <button
            onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900">Price Summary</h3>
            {showPriceBreakdown ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {showPriceBreakdown && (
            <div className="mt-4 space-y-3">
              {/* Base price breakdown */}
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  ${bookingData?.basePrice || 0} x {bookingData?.adults || 0} adults
                </span>
                <span className="text-gray-900">
                  ${(bookingData?.basePrice || 0) * (bookingData?.adults || 0)}
                </span>
              </div>

              {(bookingData?.children || 0) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    ${Math.round((bookingData?.basePrice || 0) * 0.7)} x {bookingData?.children} children
                  </span>
                  <span className="text-gray-900">
                    ${Math.round((bookingData?.basePrice || 0) * 0.7 * (bookingData?.children || 0))}
                  </span>
                </div>
              )}

              {bookingData?.addOns?.map((addon, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <span className="text-gray-600">{addon?.name}</span>
                  <span className="text-gray-900">+${addon?.price}</span>
                </div>
              ))}

              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="text-gray-900">${subtotal?.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Service fee</span>
                  <span className="text-gray-900">${serviceFee?.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Taxes</span>
                  <span className="text-gray-900">${taxes?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex justify-between">
              <span className="text-lg font-semibold text-gray-900">Total</span>
              <span className="text-lg font-semibold text-gray-900">
                ${total?.toFixed(2)} USD
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              All taxes and fees included
            </p>
          </div>
        </div>
      </div>

      {/* Refund Policy */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Info className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Refund Policy</h3>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-green-600 font-medium">Full Refund</span>
              <span className="text-gray-600">{refundPolicy?.fullRefund}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-yellow-600 font-medium">Partial Refund</span>
              <span className="text-gray-600">{refundPolicy?.partialRefund}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-red-600 font-medium">No Refund</span>
              <span className="text-gray-600">{refundPolicy?.noRefund}</span>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Protection:</strong> Your booking is covered by our Travel Protection Plan. 
              Cancel for any reason up to 24 hours before departure.
            </p>
          </div>
        </div>
      </div>

      {/* Customer Support */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Need Help?</h4>
        <div className="space-y-2 text-sm text-gray-600">
          <div>📞 24/7 Support: +1 (555) 123-4567</div>
          <div>💬 Live chat available</div>
          <div>📧 support@tourease.com</div>
        </div>
      </div>

      {/* Mobile Sticky Summary */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 -mx-6 lg:hidden">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              ${total?.toFixed(2)}
            </div>
            <div className="text-sm text-gray-500">Total including taxes</div>
          </div>
          <Button className="bg-green-600 hover:bg-green-700">
            Complete Payment
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;